/**
 * Altar de Shiny Cards — UI (Bloco 1).
 * Consome capture-balance.mjs (contrato HANDOFF).
 */
import { t, formatNumber, onLocaleChange } from '../i18n/index.js';
import {
  SHINY_CARD_ALTAR_COST as COST,
  shinyEncounterChance as encounterChance,
  shinyCaptureChance as captureChance,
  rollShinyCardDrop as cardDrop,
} from '../capture-balance.mjs';

export const SHINY_CARD_ALTAR_COST = COST ?? 50;
export const shinyEncounterChance = encounterChance;
export const shinyCaptureChance = captureChance;
export const rollShinyCardDrop = cardDrop;

const esc = (v) => String(v ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

let root = null;
let hooks = {
  getCards: () => 0,
  getSeenSpecies: () => [],
  pokemonImage: () => '',
  title: (s) => s,
  onInvoke: () => {},
  showToast: () => {},
};
const ui = { open: false, query: '', selected: '' };

function eligibleSpecies() {
  const q = ui.query.trim().toLowerCase();
  return hooks.getSeenSpecies().filter((row) => {
    if (!q) return true;
    return String(row.slug).includes(q) || String(row.name || '').toLowerCase().includes(q);
  });
}

function render() {
  if (!root) return;
  const activeSearch = document.activeElement?.id === 'altarSearch';
  const activeSearchStart = activeSearch ? document.activeElement.selectionStart : null;
  const activeSearchEnd = activeSearch ? document.activeElement.selectionEnd : null;
  const gridScrollTop = root.querySelector('.altar-species-grid')?.scrollTop || 0;
  const cards = hooks.getCards();
  const cost = SHINY_CARD_ALTAR_COST;
  const list = eligibleSpecies();
  const selected = hooks.getSeenSpecies().find((r) => r.slug === ui.selected) || null;
  const canInvoke = !!selected && cards >= cost;

  root.innerHTML = `<div class="altar-overlay-card ui-card" role="dialog" aria-modal="true" aria-labelledby="altarTitle">
    <button type="button" class="dialog-close" data-altar-action="close" aria-label="${esc(t('altar.close'))}">&times;</button>
    <header class="altar-head ui-head">
      <span class="eyebrow">${esc(t('altar.eyebrow'))}</span>
      <h2 id="altarTitle">${esc(t('altar.title'))}</h2>
      <p>${esc(t('altar.intro'))}</p>
      <p class="altar-steps">${esc(t('altar.steps'))}</p>
    </header>
    <div class="altar-cards-bar">
      <span>${esc(t('altar.cards'))}</span>
      <b>${esc(t('altar.cardsOwned', { count: formatNumber(cards) }))}</b>
      <em>${esc(t('altar.cost', { cost: formatNumber(cost) }))}</em>
    </div>
    <div class="altar-layout">
      <aside class="altar-preview">
        <div class="altar-preview-frame ${selected ? 'has-species' : ''}">
          ${selected ? `<img src="${esc(hooks.pokemonImage(selected.slug))}" alt="">` : '<span>✦</span>'}
        </div>
        <b>${selected ? esc(hooks.title(selected.slug)) : esc(t('altar.pickSpecies'))}</b>
        ${selected ? `<small>${esc(t('shiny.guaranteed'))}</small>` : ''}
        <button type="button" class="altar-invoke ui-btn-primary" data-altar-action="invoke" ${canInvoke ? '' : 'disabled'}>
          ${esc(t('altar.invoke'))}
        </button>
        ${!selected ? `<p class="altar-hint">${esc(t('altar.needSpecies'))}</p>` : cards < cost ? `<p class="altar-hint">${esc(t('altar.needCards'))}</p>` : ''}
      </aside>
      <div class="altar-picker">
        <label class="altar-search"><span>⌕</span><input id="altarSearch" value="${esc(ui.query)}" placeholder="${esc(t('altar.search'))}"></label>
        <div class="altar-species-grid">
          ${list.length ? list.map((row) => `<button type="button" class="altar-species ${row.slug === ui.selected ? 'selected' : ''}" data-altar-species="${esc(row.slug)}">
            <img src="${esc(hooks.pokemonImage(row.slug))}" alt="">
            <b>${esc(hooks.title(row.slug))}</b>
          </button>`).join('') : `<div class="altar-empty">${esc(t('altar.pickSpecies'))}</div>`}
        </div>
      </div>
    </div>
  </div>`;
  const nextSearch = root.querySelector('#altarSearch');
  if (activeSearch && nextSearch) {
    nextSearch.focus();
    const end = Math.min(nextSearch.value.length, activeSearchEnd ?? nextSearch.value.length);
    nextSearch.setSelectionRange(Math.min(activeSearchStart ?? end, end), end);
  }
  const nextGrid = root.querySelector('.altar-species-grid');
  if (nextGrid) nextGrid.scrollTop = gridScrollTop;
}

function bind() {
  root.addEventListener('click', (event) => {
    if (event.target === root) { closeAltar(); return; }
    if (event.target.closest('[data-altar-action="close"]')) { closeAltar(); return; }
    const pick = event.target.closest('[data-altar-species]');
    if (pick) { ui.selected = pick.dataset.altarSpecies; render(); return; }
    const invoke = event.target.closest('[data-altar-action="invoke"]');
    if (invoke && !invoke.disabled) {
      const slug = ui.selected;
      if (!slug) return;
      const complete = (result) => {
        if (result?.ok) {
          hooks.showToast(t('altar.invoked', { name: hooks.title(slug) }));
          closeAltar();
        } else if (result?.reason === 'cards') hooks.showToast(t('altar.needCards'));
        else if (result?.reason === 'species') hooks.showToast(t('altar.needSpecies'));
        else hooks.showToast(t('atlas.startFailed'));
      };
      try {
        const result = hooks.onInvoke(slug, SHINY_CARD_ALTAR_COST);
        if (result && typeof result.then === 'function') {
          invoke.disabled = true;
          Promise.resolve(result)
            .then(complete)
            .catch(() => complete({ ok: false, reason: 'sync' }))
            .finally(() => { if (ui.open) render(); });
        } else {
          complete(result);
        }
      } catch {
        complete({ ok: false, reason: 'sync' });
      }
    }
  });
  root.addEventListener('input', (event) => {
    if (event.target.id === 'altarSearch') { ui.query = event.target.value; render(); }
  });
}

export function initAltarUI(options = {}) {
  hooks = { ...hooks, ...options };
  root = document.createElement('div');
  root.id = 'altarOverlay';
  root.className = 'altar-overlay hidden';
  document.body.append(root);
  bind();
  onLocaleChange(() => { if (ui.open) render(); });
  addEventListener('keydown', (event) => { if (event.key === 'Escape' && ui.open) closeAltar(); });
}

export function openAltar() {
  if (!root || ui.open) return;
  ui.open = true;
  root.classList.remove('hidden');
  document.body.classList.add('altar-open');
  render();
}

export function closeAltar() {
  if (!ui.open) return;
  ui.open = false;
  root.classList.add('hidden');
  document.body.classList.remove('altar-open');
}

export function isAltarOpen() {
  return ui.open;
}
